import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Alert, Button, Col, Container, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import {
  AdminBattleBet,
  AdminBattleBetSummary,
  getAdminBattleBetSummary,
  getAdminBattleBets,
  payoutAdminBet,
  payoutAdminBets,
  refundAdminBet,
  refundAdminBets,
} from "../../../helpers/api/admin/bet";
import { useAuthStore } from "../../../store/useAuthStore";

type BattleRouteState = {
  battle?: {
    id?: string | number;
    title?: string;
    status?: string;
    startTime?: string;
    updatedAt?: string;
  };
};

type ConfirmationState = {
  action: "refund" | "pay";
  betIds: string[];
  label: string;
  points: number;
} | null;

const emptySummary: AdminBattleBetSummary = {
  battleId: "",
  totalStakedPoints: 0,
  winnersCount: 0,
  winnersVolume: 0,
  losersCount: 0,
  losersVolume: 0,
  refundedVolume: 0,
  refundedBetsCount: 0,
};

const formatPoints = (value?: number | null) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return value.toLocaleString("en-US");
};

const shortenWallet = (value?: string | null) => {
  if (!value) return "-";
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const normalize = (value?: string | null) => String(value ?? "").toLowerCase();

const getBetWallet = (bet: AdminBattleBet) => bet.user?.walletAddress || bet.user?.username || bet.userId || "-";

const getBetSide = (bet: AdminBattleBet) => bet.participation?.character?.name || bet.participationId || "-";

const isRefundableBet = (bet: AdminBattleBet) => !["refunded", "paid"].includes(normalize(bet.status));

const isPayableBet = (bet: AdminBattleBet) => normalize(bet.status) === "won";

const getStatusClassName = (status: string) => {
  const normalized = normalize(status);
  if (normalized === "won") return "admin-bets-status admin-bets-status--won";
  if (normalized === "paid") return "admin-bets-status admin-bets-status--paid";
  if (normalized === "lost") return "admin-bets-status admin-bets-status--lost";
  if (normalized === "refunded") return "admin-bets-status admin-bets-status--refunded";
  return "admin-bets-status";
};

const getPayoutPoints = (bet: AdminBattleBet) => bet.payoutPoints;

const battleWorkflowDiagram = `┌────────────┐
│  PENDING   │  (Battle created)
└─────┬──────┘
      │────────────────────────────┐
      ▼                            │
┌────────────┐                     │
│   ACTIVE   │───► Open bets       │
└─────┬──────┘                     │
      │────────────────────────────┐
      │                            │
      │                            ▼
      │                     ┌──────────────┐
      │                     │  CANCELLED   │◄─── (cancelled battle)
      ▼                     └──────────────┘
┌────────────┐
│   START    │───► Lock bets
└─────┬──────┘
      │
      ▼
┌────────────┐
│  FINISHED  │
└─────┬──────┘
      │
      ▼
┌────────────┐
│ VALIDATED  │───► Trigger reward processing
└─────┬──────┘
      │
      ▼
┌────────────┐
│  ARCHIVED  │  (Final state)
└────────────┘`;

const betWorkflowDiagram = `┌────────────┐
│  PLACED    │  (user bets and stakes points)
└─────┬──────┘
      │
      ▼
┌────────────┐
│  LOCKED    │  (user cannot cancel bet)
└─────┬──────┘
      │
      ▼
┌────────────────┐
│ WAITING_RESULT │  (battle in progress)
└─────┬──────────┘
      │
      ├──────────────┬───────────────┐
      │              │               │
      ▼              ▼               ▼
┌────────────┐  ┌────────────┐  ┌────────────┐
│    WON     │  │    LOST    │  │  REFUNDED  │
└─────┬──────┘  └────────────┘  └────────────┘
      │
      ▼
┌────────────┐
│   PAID     │  (reward sent)
└────────────┘`;

const BattleBets = () => {
  document.title = "Admin Colosseum | Battle bets";

  const { battleId = "" } = useParams();
  const location = useLocation();
  const { isAdmin } = useAuthStore();
  const battle = (location.state as BattleRouteState | null)?.battle;
  const battleStatus = normalize(battle?.status);
  const isActiveBattle = battleStatus === "active";
  const isValidatedBattle = battleStatus === "validated";

  const [bets, setBets] = useState<AdminBattleBet[]>([]);
  const [summary, setSummary] = useState<AdminBattleBetSummary>(emptySummary);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBetIds, setSelectedBetIds] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");
  const [confirmation, setConfirmation] = useState<ConfirmationState>(null);
  const [isWorkflowHelpOpen, setIsWorkflowHelpOpen] = useState<boolean>(false);

  const closeWorkflowHelp = () => setIsWorkflowHelpOpen(false);

  const loadBattleBets = async () => {
    if (!battleId) return;
    setLoading(true);
    setError(null);
    try {
      const [nextBets, nextSummary] = await Promise.all([
        getAdminBattleBets(battleId),
        getAdminBattleBetSummary(battleId),
      ]);
      setBets(nextBets);
      setSummary(nextSummary || emptySummary);
      setSelectedBetIds([]);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Failed to load battle bets");
      setBets([]);
      setSummary(emptySummary);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBattleBets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleId]);

  const filteredBets = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return bets;
    return bets.filter((bet) => {
      const wallet = getBetWallet(bet).toLowerCase();
      const side = getBetSide(bet).toLowerCase();
      return wallet.includes(term) || side.includes(term) || normalize(bet.status).includes(term);
    });
  }, [bets, search]);

  const selectedBets = useMemo(
    () => bets.filter((bet) => selectedBetIds.includes(bet.id)),
    [bets, selectedBetIds],
  );

  const selectedRefundableBets = selectedBets.filter(isRefundableBet);
  const selectedPayableBets = selectedBets.filter(isPayableBet);
  const allVisibleSelected = filteredBets.length > 0 && filteredBets.every((bet) => selectedBetIds.includes(bet.id));
  const winningSide = bets.find((bet) => bet.participation?.isWinner)?.participation?.character?.name || "-";
  const totalPayout = bets.reduce((total, bet) => total + (["won", "paid"].includes(normalize(bet.status)) ? getPayoutPoints(bet) : 0), 0);
  const selectedPayout = selectedPayableBets.reduce((total, bet) => total + getPayoutPoints(bet), 0);
  const selectedRefund = selectedRefundableBets.reduce((total, bet) => total + bet.stakedPoints, 0);

  const toggleBetSelection = (betId: string) => {
    setSelectedBetIds((current) => (
      current.includes(betId) ? current.filter((id) => id !== betId) : [...current, betId]
    ));
  };

  const toggleVisibleSelection = () => {
    if (allVisibleSelected) {
      setSelectedBetIds((current) => current.filter((id) => !filteredBets.some((bet) => bet.id === id)));
      return;
    }

    setSelectedBetIds((current) => Array.from(new Set([...current, ...filteredBets.map((bet) => bet.id)])));
  };

  const openConfirmation = (action: "refund" | "pay", targetBets: AdminBattleBet[]) => {
    const points = action === "pay"
      ? targetBets.reduce((total, bet) => total + getPayoutPoints(bet), 0)
      : targetBets.reduce((total, bet) => total + bet.stakedPoints, 0);

    setConfirmation({
      action,
      betIds: targetBets.map((bet) => bet.id),
      label: targetBets.length === 1 ? shortenWallet(getBetWallet(targetBets[0])) : `${targetBets.length} bets`,
      points,
    });
  };

  const confirmAction = async () => {
    if (!confirmation || !battleId) return;

    setActionLoading(true);
    setError(null);
    try {
      if (confirmation.action === "pay") {
        if (confirmation.betIds.length === 1) await payoutAdminBet(confirmation.betIds[0]);
        else await payoutAdminBets(battleId, confirmation.betIds);
      } else {
        if (confirmation.betIds.length === 1) await refundAdminBet(confirmation.betIds[0]);
        else await refundAdminBets(battleId, confirmation.betIds);
      }
      setConfirmation(null);
      await loadBattleBets();
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (!isAdmin()) {
    return <Navigate to={{ pathname: "/dashboard" }} />;
  }

  return (
    <div className="page-content admin-bets-page" data-cy="admin-battle-bets-page">
      <Container fluid>
        <div className="admin-bets-heading">
          <div>
            <Link to="/admin/battles" className="admin-bets-back">← Battles</Link>
            <h4 className="mb-1">
              Reward bets — <span className="gradient-text-brand">{battle?.title || `Battle ${battleId}`}</span>
            </h4>
            <p className="text-muted mb-0">
              Status: <span className="text-uppercase">{battle?.status || "unknown"}</span>
              {winningSide !== "-" && <> · Winner: <span className="neon-text-cyan">{winningSide}</span></>}
            </p>
          </div>
          <div className="admin-bets-heading-actions">
            <Button
              type="button"
              className="admin-bets-help-button"
              aria-label="Open battle and bet workflow help"
              title="Battle and bet workflow help"
              onClick={() => setIsWorkflowHelpOpen(true)}
              data-cy="admin-bets-workflow-help"
            >
              <i className="ri-question-mark" aria-hidden="true" />
            </Button>
            <span className="admin-bets-live-badge">Awaiting payout</span>
          </div>
        </div>

        {error && <Alert color="danger" role="alert">{error}</Alert>}

        <Row className="g-3 admin-bets-summary">
          <Col xs={6} xl>
            <div className="admin-bets-stat">
              <span>Total pool</span>
              <strong>{formatPoints(summary.totalStakedPoints)} <small>PTS</small></strong>
            </div>
          </Col>
          <Col xs={6} xl>
            <div className="admin-bets-stat">
              <span>Winners</span>
              <strong>{formatPoints(summary.winnersCount)} <small>bets</small></strong>
            </div>
          </Col>
          <Col xs={6} xl>
            <div className="admin-bets-stat">
              <span>Losers</span>
              <strong>{formatPoints(summary.losersCount)} <small>bets</small></strong>
            </div>
          </Col>
          <Col xs={6} xl>
            <div className="admin-bets-stat">
              <span>Refunded</span>
              <strong>{formatPoints(summary.refundedVolume)} <small>PTS</small></strong>
            </div>
          </Col>
          <Col xs={12} xl>
            <div className="admin-bets-stat">
              <span>Total payout</span>
              <strong>{formatPoints(totalPayout)} <small>PTS</small></strong>
            </div>
          </Col>
        </Row>

        <div className="admin-bets-bulkbar">
          <div>
            <strong>{selectedBetIds.length}</strong> selected
            {selectedBetIds.length > 0 && (
              <span className="text-muted ms-2">
                · {formatPoints(selectedPayout || selectedRefund)} PTS in current action scope
              </span>
            )}
          </div>
          <div className="admin-bets-actions">
            <Button
              color="light"
              disabled={actionLoading || selectedRefundableBets.length === 0 || (!isActiveBattle && !isValidatedBattle)}
              onClick={() => openConfirmation("refund", selectedRefundableBets)}
              data-cy="bulk-refund-bets"
            >
              <i className="ri-refund-line me-1" aria-hidden="true" /> Refund bets
            </Button>
            {isValidatedBattle && (
              <Button
                color="primary"
                disabled={actionLoading || selectedPayableBets.length === 0}
                onClick={() => openConfirmation("pay", selectedPayableBets)}
                data-cy="bulk-pay-bets"
              >
                <i className="ri-arrow-up-line me-1" aria-hidden="true" /> Pay out winners
              </Button>
            )}
          </div>
        </div>

        <div className="admin-bets-toolbar">
          <span>{filteredBets.length} bets</span>
          <Input
            type="search"
            placeholder="Search wallet (0x...)"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            data-cy="admin-bets-search"
          />
        </div>

        <div className="table-responsive admin-bets-table-wrap">
          <table className="table align-middle admin-bets-table">
            <thead>
              <tr>
                <th>
                  <Input
                    type="checkbox"
                    aria-label="Select visible bets"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    data-cy="admin-bets-select-all"
                  />
                </th>
                <th>Wallet</th>
                <th>Bet on</th>
                <th>Stake</th>
                <th>Payout</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-5">
                    <Spinner size="sm" className="me-2" /> Loading bets...
                  </td>
                </tr>
              ) : filteredBets.length > 0 ? (
                filteredBets.map((bet) => {
                  const canRefund = isRefundableBet(bet) && (isActiveBattle || isValidatedBattle);
                  const canPay = isValidatedBattle && isPayableBet(bet);
                  const selected = selectedBetIds.includes(bet.id);
                  return (
                    <tr key={bet.id} className={selected ? "admin-bets-row-selected" : ""} data-cy="admin-bets-row">
                      <td>
                        <Input
                          type="checkbox"
                          aria-label={`Select bet ${bet.id}`}
                          checked={selected}
                          onChange={() => toggleBetSelection(bet.id)}
                        />
                      </td>
                      <td title={getBetWallet(bet)}>{shortenWallet(getBetWallet(bet))}</td>
                      <td>
                        <span className={bet.participation?.isWinner ? "neon-text-cyan" : "neon-text-purple"}>
                          • {getBetSide(bet)}
                        </span>
                      </td>
                      <td>{formatPoints(bet.stakedPoints)} <span className="text-muted">PTS</span></td>
                      <td className="text-success">
                        {getPayoutPoints(bet) > 0 ? `+${formatPoints(getPayoutPoints(bet))}` : "-"}
                      </td>
                      <td><span className={getStatusClassName(bet.status)}>{bet.status}</span></td>
                      <td>
                        <div className="admin-bets-actions">
                          {canPay && (
                            <Button color="primary" size="sm" onClick={() => openConfirmation("pay", [bet])} data-cy="pay-bet">
                              Pay
                            </Button>
                          )}
                          {canRefund && (
                            <Button color="light" size="sm" onClick={() => openConfirmation("refund", [bet])} data-cy="refund-bet">
                              Refund
                            </Button>
                          )}
                          {!canPay && !canRefund && <span className="text-muted">-</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-5">No bets found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Container>

      <Modal
        isOpen={isWorkflowHelpOpen}
        toggle={closeWorkflowHelp}
        size="xl"
        scrollable
        centered
        fade={false}
        contentClassName="admin-workflow-modal"
      >
        <ModalHeader toggle={closeWorkflowHelp}>Battle and bet workflows</ModalHeader>
        <ModalBody className="admin-workflow-content" data-cy="admin-bets-workflow-content">
          <section aria-labelledby="battle-workflow-title">
            <h2 id="battle-workflow-title">Battle workflow</h2>
            <pre aria-label="Battle status workflow"><code>{battleWorkflowDiagram}</code></pre>

            <h3>FINISHED</h3>
            <p>As soon as the battle reaches its <code>startTime + durationSec</code>, the sporting event is over and the battle status becomes <code>finished</code>.</p>
            <p>The raw result is known, for example Fighter A won, but it has not yet been officially validated by the platform. The result is considered provisional and no reward is paid until it is validated.</p>

            <h3>VALIDATED</h3>
            <p>The admin, or an official referee, confirms the winner and the status becomes <code>validated</code>. This validation triggers reward calculation, as described in the bet workflow, and winning users become eligible for a points credit.</p>
            <p><strong>Business purpose:</strong> prevent disputes such as video evidence showing a different outcome or a later disqualification.</p>

            <h3>ARCHIVED</h3>
            <p>The battle becomes <code>archived</code>. No further action is possible: no bets, no standard disputes, and historical viewing only. This is the final state visible to everyone.</p>

            <h3>CANCELLED</h3>
            <p>At any time before result validation, the battle can become <code>cancelled</code> when the fight did not take place, fraud occurred, or another issue prevents a clear winner from being determined.</p>
            <p><strong>Direct business consequence:</strong> every associated bet must be refunded in full. Nobody wins and nobody loses.</p>
          </section>

          <section aria-labelledby="bet-workflow-title">
            <h2 id="bet-workflow-title">Bet workflow</h2>
            <p>
              Reference: <a href="https://chatgpt.com/share/69210b01-fee0-8013-b2c2-346d03fdbf96" target="_blank" rel="noreferrer">shared workflow discussion</a>.
            </p>
            <pre aria-label="Bet status workflow"><code>{betWorkflowDiagram}</code></pre>

            <h3>Step 1: Place a bet</h3>
            <p>The user chooses a fighter, selects the amount to stake in points, credits, or tokens, and confirms the bet.</p>
            <p><strong>Business rule:</strong> betting is blocked unless the battle is <code>active</code>.</p>

            <h3>Step 2: PLACED</h3>
            <p>The bet becomes <code>placed</code>. The stake is deducted from the user's balance so the same balance cannot be staked on multiple battles. The bet is locked against unrestricted changes.</p>
            <p><strong>Business purpose:</strong> prevent users from withdrawing a stake when the odds become unfavorable.</p>

            <h3>Step 3: LOCKED</h3>
            <p>When betting closes just before the fight, every bet becomes <code>locked</code>. The stake and selected winner are fixed, and the user cannot cancel.</p>
            <p><strong>Business purpose:</strong> prevent in-match cheating by switching sides after seeing who is winning.</p>

            <h3>Step 4: Battle in progress</h3>
            <p>While the fight is in progress, bets remain <code>locked</code>. No user action occurs during this period.</p>

            <h3>Step 5: WAITING_RESULT</h3>
            <p>When the battle becomes <code>finished</code>, each bet becomes <code>waiting_result</code>. The winner in the ring is known but has not been officially validated in the system, so no reward is paid yet.</p>
            <p><strong>Business purpose:</strong> avoid paying too quickly for a result that can still be disputed.</p>

            <h3>Step 6: WON / LOST</h3>
            <p>When the battle becomes <code>validated</code>, each bet is resolved. Winning bets become <code>won</code> and losing bets become <code>lost</code>. The amount owed to each winner is now known.</p>

            <h3>Step 7: PAID</h3>
            <p>For every <code>won</code> bet, the reward is credited to the user's balance and the bet becomes <code>paid</code>. Nothing is credited for a <code>lost</code> bet, which remains in that state permanently.</p>
            <p><strong>Business purpose:</strong> keep a clear record of the points earned from each battle.</p>

            <h3>Special case: Refund</h3>
            <p>If the battle becomes <code>cancelled</code>, all active bets in <code>placed</code>, <code>locked</code>, or <code>waiting_result</code> become <code>refunded</code>. The stake is returned to the user, and no bet becomes <code>won</code> or <code>lost</code>.</p>
          </section>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeWorkflowHelp} data-cy="close-admin-bets-workflow-help">Close</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!confirmation} toggle={() => !actionLoading && setConfirmation(null)} centered fade={false}>
        <ModalHeader toggle={() => !actionLoading && setConfirmation(null)}>
          Confirm {confirmation?.action === "pay" ? "payout" : "refund"}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            You are about to {confirmation?.action === "pay" ? "pay out" : "refund"} <strong>{confirmation?.label}</strong>.
          </p>
          <p className="text-muted mb-0">
            Estimated points: <strong>{formatPoints(confirmation?.points)} PTS</strong>. This admin action should be idempotent and audit logged by the API.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" disabled={actionLoading} onClick={() => setConfirmation(null)}>Cancel</Button>
          <Button color="primary" disabled={actionLoading} onClick={confirmAction} data-cy="confirm-admin-bet-action">
            {actionLoading ? <><Spinner size="sm" className="me-2" /> Processing...</> : "Confirm"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default BattleBets;
